type ToastType = 'info' | 'success' | 'warning' | 'error' | 'loading';

interface ToastAction {
  label: string;
  onClick?: () => void;
}

interface ToastOptions {
  /** Reuse an id to update that toast in place rather than stacking a second one. */
  id?: string;
  /**
   * Which `Toaster` shows it, named by that `Toaster`'s `id`. Leave it off for the one at the app root.
   * A toast raised into a name nothing is mounted under is never shown and never times out.
   */
  toaster?: string;
  description?: string;
  /** Each type carries its own icon. Leave it off for a plain toast that carries none. */
  type?: ToastType;
  /**
   * Milliseconds before it dismisses itself. `0` keeps it up until something dismisses it,
   * which is the default for a toast carrying an action.
   */
  duration?: number;
  action?: ToastAction;
  /** Set `false` to drop the close button, for a toast only its action should end. */
  dismissible?: boolean;
  /**
   * Called once, as the toast starts its exit, however that exit was started: the close button, the
   * action, a clock running out, `toast.dismiss`, or being pushed out by a fourth. Rewriting the id
   * is not an exit and does not call it, and neither does tearing the `Toaster` down.
   */
  onDismiss?: () => void;
}

interface ToastPromiseMessages<T> {
  loading: string;
  success: string | ((value: T) => string);
  error: string | ((error: unknown) => string);
}

interface ToastRecord {
  id: string;
  toaster?: string;
  title: string;
  description?: string;
  type?: ToastType;
  duration: number;
  action?: ToastAction;
  dismissible: boolean;
  onDismiss?: () => void;
  /** Set while the exit animation runs. The record is dropped once it finishes. */
  closing: boolean;
  /** Bumped on every show under the same id, so the toaster puts its clock back to the start. */
  shown: number;
}

const DEFAULT_DURATION = 4000;
const TOAST_LIMIT = 3;

let lastToastId = 0;

const toasts = new Map<string, ToastRecord>();
const listeners = new Set<() => void>();

/* Cached rather than built per read: `useSyncExternalStore` compares snapshots by identity. */
let snapshot: ToastRecord[] = [];

const emit = () => {
  snapshot = [...toasts.values()];
  for (const listener of listeners) {
    listener();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;

/* Skips anything a caller is already standing under, so a generated id never rewrites its toast. */
const nextToastId = () => {
  let id: string;

  do {
    id = `toast-${++lastToastId}`;
  } while (toasts.has(id));

  return id;
};

/* Hands the records back rather than calling their handlers, so a handler that raises or dismisses
   a toast of its own runs against a store that has already settled and emitted. */
const startClosing = (records: (ToastRecord | undefined)[]) => {
  const closed: ToastRecord[] = [];

  for (const record of records) {
    if (record && !record.closing) {
      toasts.set(record.id, { ...record, closing: true });
      closed.push(record);
    }
  }

  return closed;
};

/* Only a rendered toast fires the `animationend` that drops its record, so a stack nothing is
   mounted under would keep every record it was ever handed. Held to the same three, oldest first. */
const dropSurplus = (toaster?: string) => {
  const closing = [...toasts.values()].filter((record) => record.closing && record.toaster === toaster);

  for (const record of closing.slice(0, Math.max(0, closing.length - TOAST_LIMIT))) {
    toasts.delete(record.id);
  }
};

/**
 * Shows a toast, or updates the one already carrying `id`, and returns that id. Rendered by the
 * `Toaster` mounted at the app root, which times it out and animates it away. Three show at once,
 * and raising another dismisses the oldest.
 */
const showToast = (title: string, options: ToastOptions = {}) => {
  const {
    id = nextToastId(),
    toaster,
    description,
    type,
    action,
    /* The stack takes the last tab stops on the page, so a keyboard user reaching an action has a
       whole document to tab through first. A clock would run out before they arrived. */
    duration = action ? 0 : DEFAULT_DURATION,
    dismissible = true,
    onDismiss,
  } = options;
  const existing = toasts.get(id);

  /* Writing an existing key keeps its place in the map, so an update is rewritten where it stands
     rather than jumping to the end of the stack. */
  toasts.set(id, {
    id,
    toaster,
    title,
    description,
    type,
    duration,
    action,
    dismissible,
    onDismiss,
    closing: false,
    shown: (existing?.shown ?? 0) + 1,
  });

  /* Dismissed rather than dropped, so what is pushed out plays the same exit as anything else. Each
     stack fills and empties on its own, so a dialog's toaster does not push out the app root's. */
  const standing = [...toasts.values()].filter((record) => !record.closing && record.toaster === toaster);
  const pushedOut = startClosing(
    standing.filter((record) => record.id !== id).slice(0, Math.max(0, standing.length - TOAST_LIMIT))
  );

  dropSurplus(toaster);
  emit();

  for (const record of pushedOut) {
    record.onDismiss?.();
  }

  return id;
};

const withType = (type: ToastType) => (title: string, options?: Omit<ToastOptions, 'type'>) =>
  showToast(title, { ...options, type });

/* Held up until something replaces it, since a spinner that times itself out leaves the work
   looking finished. A duration that was asked for still wins. */
const loading = (title: string, options?: Omit<ToastOptions, 'type'>) =>
  showToast(title, { duration: 0, ...options, type: 'loading' });

const resolveMessage = <T>(message: string | ((value: T) => string), value: T) =>
  typeof message === 'function' ? message(value) : message;

/**
 * Follows a promise through one toast: spinning while it runs, then replaced in place by the
 * outcome. The rejection is rethrown rather than swallowed, so a call left unawaited raises an
 * unhandled rejection. Await it, or catch it.
 */
const promise = async <T>(
  pending: Promise<T>,
  messages: ToastPromiseMessages<T>,
  options?: Pick<ToastOptions, 'toaster'>
) => {
  const id = loading(messages.loading, options);

  try {
    const value = await pending;
    showToast(resolveMessage(messages.success, value), { ...options, id, type: 'success' });
    return value;
  } catch (error) {
    showToast(resolveMessage(messages.error, error), { ...options, id, type: 'error' });
    throw error;
  }
};

/** Starts the exit animation on one toast by id, or on every toast when called with nothing. */
const dismiss = (id?: string) => {
  const dismissed = startClosing(id === undefined ? [...toasts.values()] : [toasts.get(id)]);

  if (dismissed.length === 0) {
    return;
  }

  emit();

  for (const record of dismissed) {
    record.onDismiss?.();
  }
};

/* Called once the exit animation has finished. Skipped where the id was shown again in the meantime,
   which revives the record rather than replacing it. */
const removeToast = (id: string) => {
  if (!toasts.get(id)?.closing) {
    return;
  }

  toasts.delete(id);
  emit();
};

/* Nothing renders a stack once its `Toaster` is gone, so what is left under that name is stale. The
   other stacks are still mounted and are left alone. */
const clearToasts = (toaster?: string) => {
  let cleared = false;

  for (const record of toasts.values()) {
    if (record.toaster === toaster) {
      toasts.delete(record.id);
      cleared = true;
    }
  }

  if (cleared) {
    emit();
  }
};

const toast = Object.assign(showToast, {
  info: withType('info'),
  loading,
  success: withType('success'),
  warning: withType('warning'),
  error: withType('error'),
  promise,
  dismiss,
});

export {
  clearToasts,
  dismiss,
  getSnapshot,
  removeToast,
  subscribe,
  type ToastAction,
  type ToastOptions,
  type ToastPromiseMessages,
  type ToastRecord,
  type ToastType,
  toast,
};
